import { AfterViewInit, Directive, HostBinding, inject, Input, OnDestroy, OnInit } from '@angular/core'
import { CmsComponentData } from '@spartacus/storefront'
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs'
import { ComponentState, ConvertedComponentData } from '../../types'
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators'
import { MediaQueries } from '../../../enums/media-queries'
import { ImageSize } from '../../../enums/image-size'
import { CmsComponentUtils } from '../../utils/cms-component-utils'
import { UcService } from '../../../enums/uc-service'
import { CmsLinkType } from '../../../enums/cms-link-type'
import { createCypressInitialize } from './cypress-helper'
import { CmsEnvironmentService } from '../../../services/cms-environment.service'

@Directive()
export class CmsBaseComponent<T extends ConvertedComponentData> implements OnInit, AfterViewInit, OnDestroy {

  // Do not use this for anything but the HostBinding
  private shouldApplyPreviewStyles: boolean = false
  @HostBinding('style.opacity') get opacity (): number {
    return this.shouldApplyPreviewStyles ? 0.5 : 1.0
  }

  readonly cmsEnvService = inject(CmsEnvironmentService)
  readonly subscription = new Subscription()
  readonly initializeCypress = createCypressInitialize()

  // data source #1:
  // if data is provided here, this is a top level component that occupies a page slot
  private componentData: CmsComponentData<T> | null = inject(CmsComponentData<T>, { optional: true })
  private stateFromComponentData = this.componentData?.data$.pipe(
    map(data => ({
      data: data,
      isChild: false
    }))
  )

  // data source #2:
  // if data is provided here, this is a child component that get's its data handed down from a parent component
  private dataFromInputSubject = new BehaviorSubject<T | null>(null)
  private stateFromInput$ = this.dataFromInputSubject.pipe(
    map(data => data ? {
      data: data,
      isChild: true
    } : null)
  )

  readonly baseState$: Observable<ComponentState<T>> = combineLatest([
    this.stateFromInput$,
    this.cmsEnvService.isFirstSpiritPreview$.pipe(distinctUntilChanged())
  ]).pipe(
    switchMap(([stateFromInput, isPreview]) => (stateFromInput ? of(stateFromInput) : this.stateFromComponentData ?? EMPTY).pipe(
      map(state => {
        const isHiddenOutsideOfPreview = !CmsComponentUtils.isComponentVisible(state.data.visibility, false)
        const isVisibleInsideAndOutsidePreview = CmsComponentUtils.isComponentVisible(state.data.visibility, isPreview)
        return {
          ...state,
          isVisible: isVisibleInsideAndOutsidePreview,
          isPreview: isPreview,
          shouldApplyPreviewStyles: isPreview && isHiddenOutsideOfPreview
        } as ComponentState<T>
      })
    ))
  )

  readonly mediaQuery = MediaQueries
  readonly imageSize = ImageSize
  readonly ucService = UcService
  readonly cmsLinkType = CmsLinkType

  @Input() set data (data: T) {
    this.dataFromInputSubject.next(data)
  }

  ngOnInit () {
    this.subscription.add(this.baseState$.subscribe(state => this.shouldApplyPreviewStyles = state.shouldApplyPreviewStyles))
    this.subscription.add(this.initializeCypress(this.baseState$.pipe(
      map(state => state.data)
    )))
  }

  ngAfterViewInit () {
    /* Keep this even though it is empty so that deriving components can call super.ngAfterViewInit().
     * If we add something here in the future, those deriving components are already prepared.
     */
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }
}
